import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money, nextTierHint } from "@/lib/api";

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, discount, net, lineDiscount } =
    useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-ink-2"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-5">
              <h2 className="font-display text-xl font-black uppercase tracking-wide">
                Your Cart <span className="text-brand-cyan">({items.length})</span>
              </h2>
              <button
                data-testid="cart-drawer-close"
                onClick={() => setOpen(false)}
                className="text-zinc-400 hover:text-white"
                aria-label="Close cart"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="border-b border-white/10 px-5 py-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-cyan">
                Free local pickup — 24 Upper Elleston Road, Kingston 16
              </p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Bulk discounts auto-apply on eligible items
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {items.length === 0 ? (
                <div className="halftone flex h-full flex-col items-center justify-center border border-dashed border-white/15 text-center">
                  <p className="font-display text-2xl font-bold uppercase text-zinc-500">Cart is empty</p>
                  <p className="mt-1 font-mono text-xs uppercase tracking-widest text-zinc-600">
                    Load it up with custom ink
                  </p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((item) => {
                    const pct = lineDiscount(item);
                    const gross = item.lineTotal != null ? item.lineTotal : item.unitPrice * item.qty;
                    const rush = item.rushFee || 0;
                    const discountBase =
                      item.discountBase != null
                        ? item.discountBase
                        : item.discountUnit != null
                          ? item.discountUnit * item.qty
                          : gross;
                    const hint = nextTierHint(item.categorySlug, item.qty);
                    return (
                      <li
                        key={item.key}
                        data-testid={`cart-item-${item.key}`}
                        className="border border-white/10 bg-ink-3 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-display text-base font-bold uppercase leading-tight">
                              {item.name}
                            </p>
                            {item.configLabel && (
                              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-cyan">
                                {item.configLabel}
                              </p>
                            )}
                            {item.artworkName && (
                              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                Artwork: {item.artworkName}
                              </p>
                            )}
                            {rush > 0 && (
                              <p
                                data-testid={`cart-item-rush-${item.key}`}
                                className="mt-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-brand-yellow"
                              >
                                ⚡ Rush Upgrade +{money(rush)}
                              </p>
                            )}
                          </div>
                          <button
                            data-testid={`cart-item-remove-${item.key}`}
                            onClick={() => removeItem(item.key)}
                            className="text-zinc-500 hover:text-brand-magenta"
                            aria-label="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {pct > 0 && (
                          <p
                            data-testid={`cart-item-discount-${item.key}`}
                            className="mt-2 inline-block border border-brand-cyan/40 bg-brand-cyan/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-brand-cyan"
                          >
                            Bulk pricing applied
                          </p>
                        )}
                        {pct === 0 && hint && (
                          <p className="mt-2 font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                            {hint}
                          </p>
                        )}
                        <div className="mt-3 flex items-center justify-between">
                          {item.lineTotal != null ? (
                            <span
                              data-testid={`cart-item-pack-${item.key}`}
                              className="font-mono text-[10px] uppercase tracking-widest text-zinc-500"
                            >
                              {item.qty} stickers — fixed pack
                            </span>
                          ) : (
                            <div className="flex items-center border border-white/15">
                              <button
                                data-testid={`cart-item-quantity-decrease-${item.key}`}
                                onClick={() => updateQty(item.key, -1)}
                                className="px-2.5 py-1 text-zinc-400 hover:text-white"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="min-w-8 text-center font-mono text-sm">{item.qty}</span>
                              <button
                                data-testid={`cart-item-quantity-increase-${item.key}`}
                                onClick={() => updateQty(item.key, 1)}
                                className="px-2.5 py-1 text-zinc-400 hover:text-white"
                                aria-label="Increase quantity"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                          <span className="font-mono text-sm font-semibold text-brand-yellow">
                            {pct > 0 && (
                              <span className="mr-2 font-normal text-zinc-500 line-through">
                                {money(gross)}
                              </span>
                            )}
                            {money(gross - (discountBase * pct) / 100 + rush)}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="border-t border-white/10 p-5">
              <div className="flex justify-between font-mono text-sm">
                <span className="uppercase tracking-widest text-zinc-400">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-bold text-white">{money(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="mt-2 flex justify-between font-mono text-sm text-brand-cyan">
                  <span data-testid="cart-discount-label" className="uppercase tracking-widest">
                    You Save — Bulk Discount
                  </span>
                  <span data-testid="cart-discount" className="font-bold">{money(discount)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="mt-2 flex justify-between border-t border-white/10 pt-2 font-mono text-sm">
                  <span className="uppercase tracking-widest text-zinc-400">Total</span>
                  <span data-testid="cart-net-total" className="font-bold text-brand-yellow">{money(net)}</span>
                </div>
              )}
              <button
                data-testid="cart-checkout-button"
                disabled={items.length === 0}
                onClick={() => {
                  setOpen(false);
                  navigate("/checkout");
                }}
                className="mt-4 flex w-full items-center justify-center gap-2 bg-brand-yellow py-3.5 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan disabled:cursor-not-allowed disabled:opacity-40"
              >
                Checkout <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
